import { useGetLeaseBalanceQuery } from '@/store/api';
import type { BalanceItem } from '@/types/residents/lease';
import { getMockLeaseBalance, refetchMockLeaseBalance } from './mockLeaseBalance';

/**
 * Fetches the current balance for a lease using the dedicated balance endpoint.
 * Works for all PMS types — the backend routes via PMSAdapterFactory.
 * Aligned with the web app's getTenantBalance / getResidentBalance pattern.
 *
 * Dev-only: `MOCK_BALANCE_DUE` in `.env` serves a canned balance instead and
 * keeps the (slow, PMS-backed) query off the wire. See mockLeaseBalance.ts.
 */
export const usePaymentBalance = (leaseId: number | string, options?: { skip?: boolean }) => {
  const leaseIdNum = typeof leaseId === 'string' ? Number(leaseId) : leaseId;
  const mock = getMockLeaseBalance();

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useGetLeaseBalanceQuery(leaseIdNum, {
    skip: options?.skip || !leaseIdNum || mock !== null,
  });

  const balanceData = mock ?? queryData;
  const balanceDue = Number(balanceData?.balanceDue || 0);
  const accountCredit = Number(balanceData?.accountCredit || 0);
  const balanceItems: BalanceItem[] | undefined = balanceData?.balanceItems;
  // The skipped query cannot be refetched (RTK throws), so hand back the mock.
  const refresh = mock ? refetchMockLeaseBalance : refetch;

  return {
    balanceDue,
    accountCredit,
    balanceItems,
    isLoading,
    error,
    refetch: refresh,
    refreshBalance: refresh,
  };
};
